import { appCopy, localDateOf, type ActionOutput } from '@zibanu/shared';
import { Badge, RecordRow } from '@zibanu/ui';
import { timeOf } from './format';

/** One row of entries.list. */
export type Entry = ActionOutput<'entries.list'>['entries'][number];

/** "today" in the books' own timezone. */
export const todayIn = (timezone: string, now: Date) => localDateOf(now, timezone);

/**
 * "Sale — Chinedu", "Payment — Chinedu", "Transport & fuel": what it was, then who.
 * A payment by its flag: lists only have the summary, and a removed payment keeps it.
 */
export function entryTitle(e: Entry): string {
  const what = e.flags.includes('settlement') ? appCopy.payment() : e.direction === 'in' ? appCopy.sale() : (e.categoryLabel ?? appCopy.expense());
  return e.counterpartyName ? `${what} — ${e.counterpartyName}` : what;
}

/** One short badge, most important first: Removed, Personal?, Which customer?, Guessed, Owing. */
export function entryBadge(e: Entry) {
  if (e.status === 'removed') return <Badge tone="neutral" icon={false}>{appCopy.badgeRemoved()}</Badge>;
  if (e.flags.includes('personal_check')) return <Badge tone="palm" icon={false}>{appCopy.badgePersonal()}</Badge>;
  if (e.flags.includes('customer_check')) return <Badge tone="palm" icon={false}>{appCopy.badgeWhichCustomer()}</Badge>;
  if (e.flags.includes('low_confidence')) return <Badge tone="palm" icon={false}>{appCopy.badgeGuessed()}</Badge>;
  if (e.owedMinor > 0) return <Badge tone="clay" icon={false}>{appCopy.badgeOwing()}</Badge>;
  return null;
}

export interface EntryRowProps {
  entry: Entry;
  meId: string;
  timezone: string;
  /** Owners' lists say who recorded each; staff's are all their own. */
  showWho: boolean;
  onOpen: () => void;
}

export function EntryRow({ entry, meId, timezone, showWho, onOpen }: EntryRowProps) {
  const who = entry.recordedBy ? (entry.recordedBy.personId === meId ? appCopy.you() : entry.recordedBy.name) : null;
  const meta = [timeOf(entry.updatedAt, timezone), showWho ? who : null].filter(Boolean).join(' · ');
  return (
    <RecordRow
      kind={entry.direction === 'in' ? 'sale' : 'expense'}
      title={entryTitle(entry)}
      meta={meta}
      minor={entry.amountMinor}
      {...(entry.channel === 'whatsapp' ? { source: 'whatsapp' as const } : {})}
      status={entryBadge(entry)}
      removed={entry.status === 'removed'}
      onClick={onOpen}
    />
  );
}
